'use client';

import { useMemo, useState } from 'react';
import { md5 } from '@/lib/browser-gencalc-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserMd5Generator() {
  const [input, setInput] = useState('');
  const [uppercase, setUppercase] = useState(false);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const digest = useMemo(() => md5(input), [input]);
  const output = uppercase ? digest.toUpperCase() : digest;

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">MD5</span>
        <h2 className="tool-live-title">MD5 hash generator</h2>
        <p className="tool-live-subtitle">
          Type or paste text to compute its 128-bit MD5 checksum instantly. Everything runs locally
          in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Text to hash</span>
              <textarea
                onChange={(event) => setInput(event.target.value)}
                placeholder="Paste text here"
                rows={8}
                value={input}
              />
            </label>
            <label className="tool-live-field">
              <span>Letter case</span>
              <select
                onChange={(event) => setUppercase(event.target.value === 'upper')}
                value={uppercase ? 'upper' : 'lower'}
              >
                <option value="lower">Lowercase (default)</option>
                <option value="upper">Uppercase</option>
              </select>
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy hash
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>MD5 hash</span>
            <textarea readOnly rows={3} value={output} />
          </label>
          <p className="tool-live-panel-note">
            MD5 is a fast checksum for detecting accidental changes, not a secure hash. Never use it
            for passwords, signatures, or anything security-sensitive; use SHA-256 or bcrypt
            instead.
          </p>
        </section>
      </div>
    </div>
  );
}
